import {
   AfterViewInit,
   Directive,
   ElementRef,
   EventEmitter,
   InjectionToken,
   Input,
   Optional,
   Output,
   ViewChild,
} from '@angular/core';
import { MatChipEvent, MatChipListbox } from '@angular/material/chips';
import { Observable, merge } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { LcsChipMultiSelectComponent } from '../multi-select/multi-select.component';

export const LCS_CHIP_GRID = new InjectionToken<LcsChipGrid<any>>('lcs-chip-grid');

export interface LcsChipGrid<TItem> {
   disabled: boolean;
   focused: boolean;
   remove: Observable<TItem>;
   value: TItem[] | null;
   chipList: MatChipListbox;
   add(value: TItem): void;
   focus(): void;
   originatesFromChip(event: MouseEvent): boolean;
}

@Directive({
    standalone: false
})
export abstract class LcsChipGridComponentBase<TItem> implements AfterViewInit, LcsChipGrid<TItem> {
   @Input() set disabled(value: boolean) {
      this._disabled = value;
      this.chipList.disabled = value;
   }

   get disabled(): boolean {
      return this._disabled;
   }

   @Output() remove = new EventEmitter<TItem>();

   @ViewChild(MatChipListbox) chipList: MatChipListbox;

   _disabled = false;

   value: TItem[] | null = null;

   get focused(): boolean {
      return this.chipList?.focused;
   }

   constructor(
      protected elementRef: ElementRef,
      @Optional() protected multiSelect: LcsChipMultiSelectComponent<TItem> | null
   ) {
      if (multiSelect) {
         multiSelect.registerChipGrid(this);
      }
   }

   ngAfterViewInit(): void {
      if (this.chipList?._chips) {
         this.chipList._chips.changes
            .pipe(
               startWith(null),
               switchMap(() => {
                  return merge(...this.chipList._chips.map((chip) => chip.removed));
               })
            )
            .subscribe((event: MatChipEvent) => {
               this.value = this.value!.filter((item) => item !== event.chip.value);
               this.remove.emit(event.chip.value);
            });
      }
   }

   add(value: TItem): void {
      this.value = this.value ? [...this.value, value] : [value];
   }

   focus(): void {
      this.chipList.focus();
   }

   /** Checks whether an event comes from inside a chip element. */
   originatesFromChip(event: MouseEvent): boolean {
      let currentElement = event.target as HTMLElement | null;

      while (currentElement && currentElement !== this.elementRef.nativeElement) {
         if (currentElement.classList.contains('mat-chip')) {
            return true;
         }

         currentElement = currentElement.parentElement;
      }

      return false;
   }

   abstract trackBy(item: TItem | null): any;
}
